import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { valueRender } from '@repo/toolkit/conditional-render'
import { DateFormatter } from '@repo/toolkit/date'
import { NumberFormatter } from '@repo/toolkit/number'
import { toFullName } from '@repo/toolkit/to-full-name'
import { useNavigator } from '@repo/toolkit/use-navigator'
import Badge from '@repo/ui/badge'
import { Column } from '@repo/ui/data-table'
import { ClipboardIcon, InformationIcon } from '@repo/ui/icons'
import Typography from '@repo/ui/typography'
import { useState } from 'react'

import { API } from '../../api/apis.config'
import CountryFlag from '../../components/elements/country-flag'
import FilterDrawer from '../../components/elements/drawers/filter-drawer'
import DynamicDataTable from '../../components/elements/dynamic-table'
import { countFilters } from '../../components/elements/dynamic-table/utils/countFilters'
import InformationToolTip from '../../components/elements/information-tooltip'
import UserNavBar from '../../components/elements/nav-bar'
import PeriodBadge from '../../components/elements/period-badge'
import { QualificationTags } from '../../components/elements/qualification-badge'
import TravelExpensesStaffFilter from '../../components/pages/travel-expenses/travel-expenses-staff-filter'
import { Paths } from '../../lib/constants/path'
import { useFilters } from '../../lib/hooks/use-filters'
import { useModalManager } from '../../lib/hooks/use-modal-manager'
import useTranslate from '../../lib/hooks/use-translate'
import {
  TravelExpensesStatusEnum,
  travelExpensesStatusEnumMapping
} from '../../lib/types/enums/affected-staff/affected-staff-status.enum'
import { staffPositionEnumMapping } from '../../lib/types/enums/staff/staff-position.enum'
import { StaffQualificationEnum } from '../../lib/types/enums/staff/staff-qualifications.enum'
import { StaffRequestProperties } from '../../lib/types/models/staff-request/properties.staff-request'
import {
  TravelExpensesStaff,
  TravelExpensesStaffsFilters
} from '../../lib/types/models/travel-expenses/travel-expenses-staff'
import {
  defaultTravelExpensesStaffStatuses,
  transformTravelExpensesStaffFiltersToUrl,
  transformUrlToTravelExpensesStaffFilters
} from '../../lib/utils/filters-mapping/travel-expenses-staff'
import { getTranslatedName } from '../../lib/utils/functions/translation.functions'

const travelExpensesInitialFilters: TravelExpensesStaffsFilters = {
  travelExpensesStatuses: [TravelExpensesStatusEnum.RECEIVED]
}

const getTravelExpensesFiltersWithDefaultStatuses = (
  filters?: TravelExpensesStaffsFilters
): TravelExpensesStaffsFilters => ({
  ...filters,
  travelExpensesStatuses: filters?.travelExpensesStatuses?.length
    ? filters.travelExpensesStatuses
    : defaultTravelExpensesStaffStatuses
})

export function TravelExpensesListPage() {
  const { navigateWithEvent } = useNavigator()
  const { t } = useTranslate()
  const [isTableLoading, setIsTableLoading] = useState(false)
  const { modalState, toggleModal, closeModel } = useModalManager<'filters'>(['filters'])

  const { selectedFilter, filter, onFilterChange, resetFilters, onSelectedFilterChange } =
    useFilters<TravelExpensesStaffsFilters>({
      initialFilters: travelExpensesInitialFilters,
      options: { allowSearchParam: true },
      transformFilterToUrl: transformTravelExpensesStaffFiltersToUrl,
      transformUrlToFilter: transformUrlToTravelExpensesStaffFilters
    })

  const getQualifications = (te: TravelExpensesStaff): StaffQualificationEnum[] => {
    if (te.affectedStaffId) return [te.affectedStaff.staffRequest.qualification]
    else if (te.missionToStaffId) return te.missionToStaff.mission.qualifications
    return []
  }

  const getTravelExpensesSentAt = (te: TravelExpensesStaff) => {
    if (te.affectedStaffId) return te.affectedStaff.travelExpensesSentAt
    else if (te.missionToStaffId) return te.missionToStaff.travelExpensesSentAt
    throw new Error('TravelExpensesStaff must have either affectedStaffId or missionToStaffId')
  }

  const getTravelExpensesStatus = (te: TravelExpensesStaff) => {
    if (te.affectedStaffId) return te.affectedStaff.travelExpensesStatus
    else if (te.missionToStaffId) return te.missionToStaff.travelExpensesStatus
    throw new Error('TravelExpensesStaff must have either affectedStaffId or missionToStaffId')
  }

  const getStaff = (te: TravelExpensesStaff) => {
    if (te.affectedStaffId) return te.affectedStaff.staff
    else if (te.missionToStaffId) return te.missionToStaff.staff
    throw new Error('TravelExpensesStaff must have either affectedStaffId or missionToStaffId')
  }

  const getTravelExpenses = (te: TravelExpensesStaff) => {
    if (te.affectedStaffId) return te.affectedStaff.travelExpenses
    else if (te.missionToStaffId) return te.missionToStaff.travelExpenses
    throw new Error('TravelExpensesStaff must have either affectedStaffId or missionToStaffId')
  }

  const getTravelExpensesCurrency = (te: TravelExpensesStaff) => {
    if (te.affectedStaffId) return te.affectedStaff.travelExpensesCurrency
    else if (te.missionToStaffId) return te.missionToStaff.travelExpensesCurrency
    throw new Error('TravelExpensesStaff must have either affectedStaffId or missionToStaffId')
  }

  const columns: Column<TravelExpensesStaff>[] = [
    {
      accessorKey: 'affectedStaff.travelExpensesSentAt || missionToStaff.travelExpensesSentAt',
      header: 'Date de réception',
      cell: ({ row }) => {
        const travelExpensesSentAt = getTravelExpensesSentAt(row.original)
        return travelExpensesSentAt ? (
          <Badge
            text={valueRender(travelExpensesSentAt, (date) => DateFormatter.DDMMYYYY(date))}
            variant="grey"
          />
        ) : (
          '-'
        )
      }
    },
    {
      accessorKey: 'lastName',
      header: 'Encadrant concerné',
      cell: ({ row }) =>
        toFullName(getStaff(row.original).firstName, getStaff(row.original).lastName, {
          options: { mode: 'lastname-firstname' }
        })
    },
    {
      accessorKey: 'position',
      header: 'Poste',
      cell: ({ row }) => {
        const position = getStaff(row.original)?.position
        if (!position) return '-'
        return t(staffPositionEnumMapping[position])
      }
    },
    {
      accessorKey: 'staffRequest.id',
      header: 'Séjour / Mission',
      cell: ({ row }) => {
        if (row.original.affectedStaffId) {
          const staffRequest = row.original.affectedStaff.staffRequest
          const period = StaffRequestProperties.getPeriod(staffRequest)
          if (!staffRequest || !period) return <span className="text-gray-500">N/A</span>
          return (
            <PeriodBadge
              period={period}
              text={
                <div className="flex items-center gap-1">
                  <CountryFlag code={staffRequest.country?.code} />
                  <Typography variant="label-medium" style={{ color: period.color }}>
                    {[
                      staffRequest.centers.map((center) => center.name).join(', '),
                      staffRequest.formulas.map((f) => getTranslatedName(f)).join(', '),
                      staffRequest.startDate && staffRequest.endDate
                        ? `${DateFormatter.DDMMYYYY(staffRequest.startDate)} à ${DateFormatter.DDMMYYYY(staffRequest.endDate)}`
                        : 'N/A'
                    ]
                      .filter(Boolean)
                      .join(' - ')}
                  </Typography>
                </div>
              }
            />
          )
        } else if (row.original.missionToStaffId) return row.original.missionToStaff.mission.title
      }
    },
    {
      accessorKey: 'qualifications',
      header: 'Qualification',
      cell: ({ row }) => <QualificationTags qualifications={getQualifications(row.original)} />
    },
    {
      accessorKey: 'travelExpenses',
      header: 'Total',
      enableSorting: false,
      headerInfo: (
        <InformationToolTip
          contentVariant="secondary"
          icon={
            <InformationIcon width="20px" height="20px" color={tailwindColors.secondary.DEFAULT} />
          }
          size="small"
          contentClassName="p-2"
        >
          <Typography variant={'label-medium'} color={tailwindColors.black.DEFAULT}>
            Si les notes de frais sont validées, le total
            <br />
            correspond à la somme des montants
            <br />
            validés, sinon il correspond à la somme des
            <br />
            montants saisis par le personnel.
          </Typography>
        </InformationToolTip>
      ),
      cell: ({ row }) => {
        const status = getTravelExpensesStatus(row.original)
        const expenses = getTravelExpenses(row.original)
        const totalAmount =
          status == TravelExpensesStatusEnum.VALIDATED
            ? expenses.reduce((sum, e) => sum + (e.validatedAmount ?? 0), 0)
            : expenses.reduce((sum, e) => sum + (e.amount ?? 0), 0)
        return (
          <Badge
            variant="primary"
            text={NumberFormatter.formatCurrency(
              totalAmount,
              getTravelExpensesCurrency(row.original),
              'narrowSymbol'
            )}
          />
        )
      }
    },
    {
      accessorKey: 'travelExpensesStatus',
      header: 'Statut',
      cell: ({ row }) => (
        <Badge
          variant={travelExpensesStatusEnumMapping[getTravelExpensesStatus(row.original)].variant}
          text={travelExpensesStatusEnumMapping[getTravelExpensesStatus(row.original)].label}
        />
      )
    }
  ]

  return (
    <div>
      <UserNavBar title={'Gestion notes de frais'} icon={ClipboardIcon} />
      <div className={'h-4'} />

      <DynamicDataTable<TravelExpensesStaff, TravelExpensesStaffsFilters>
        title={'Liste des notes de frais'}
        columns={columns}
        allowSearchParam
        endpoint={API.travelExpenses.getStaffs.url}
        filters={selectedFilter}
        filtersLength={countFilters(selectedFilter)}
        onFiltersClick={() => toggleModal('filters')}
        onLoadingChange={setIsTableLoading}
        onRowClick={(te, event) => {
          if (te.affectedStaffId)
            navigateWithEvent(
              Paths.AFFECTED_STAFF_EXPENSE_REPORT_DETAILS_PAGE.replace(
                ':id',
                te.affectedStaffId.toString()
              ),
              event
            )
          else if (te.missionToStaffId)
            navigateWithEvent(
              Paths.MISSION_TO_STAFF_EXPENSE_REPORT_DETAILS_PAGE.replace(
                ':id',
                te.missionToStaffId.toString()
              ),
              event
            )
        }}
        permissions={{ delete: false }}
      />

      <FilterDrawer<TravelExpensesStaffsFilters>
        isOpen={modalState.filters}
        selectedFilter={selectedFilter}
        isLoading={isTableLoading}
        onClose={() => closeModel('filters')}
        onReset={resetFilters}
        onApply={() => onSelectedFilterChange(getTravelExpensesFiltersWithDefaultStatuses(filter))}
        onFilterChange={onFilterChange}
      >
        <TravelExpensesStaffFilter filter={filter} onFilterChange={onFilterChange} />
      </FilterDrawer>
    </div>
  )
}
